import type { api } from '@vrplatform/data';
import { day, isUUID } from '@vrplatform/utils';
import type {
  OwnerStatementSummariesDetailData,
  OwnerStatementSummariesDetailTableRow,
  OwnerStatementSummariesDetailTableSection,
} from './useOwnerStatementSummariesDetailQuery';

const DESCRIPTION_COLUMN_TITLE = 'Description';
const DATE_COLUMN_TITLE = 'Date';
const EXPENSES_SECTION_TITLE = 'Expenses';
const PAYOUTS_SECTION_TITLE = 'Payouts';
const TRANSACTION_IDENTIFIER_PATTERN = /^(bill|payout)\s+id#?\s*(.+)$/i;
const TRANSACTION_REFERENCE_PATTERN = /^(bill|payout|pay|xfr)[-\s#_]/i;

export type OwnerStatementSummariesDetailTransaction =
  api['/transactions']['get']['responses'][200]['content']['application/json']['data'][number];

export type OwnerStatementSummariesDetailTransactionsById = Record<
  string,
  OwnerStatementSummariesDetailTransaction
>;

export type OwnerStatementSummariesDetailTransactionLookup = {
  ids: string[];
  shortRefs: string[];
  uniqueRefs: string[];
};

const normalizeColumnTitle = (value: string | undefined) =>
  value?.trim().toLowerCase() || '';

const isDescriptionColumnTitle = (value: string | undefined) =>
  normalizeColumnTitle(value) ===
  normalizeColumnTitle(DESCRIPTION_COLUMN_TITLE);

const isDateColumnTitle = (value: string | undefined) =>
  normalizeColumnTitle(value) === normalizeColumnTitle(DATE_COLUMN_TITLE);

const isTransactionBreakdownView = (
  view: OwnerStatementSummariesDetailData['resolvedView']
) => ['reservation', 'bookingChannel'].includes(view);

const isExpensesOrPayoutsSectionTitle = (value: string | undefined) =>
  [
    normalizeColumnTitle(EXPENSES_SECTION_TITLE),
    normalizeColumnTitle(PAYOUTS_SECTION_TITLE),
  ].includes(normalizeColumnTitle(value));

const getTransactionIdentifier = (title: string | undefined) => {
  const match = title?.trim().match(TRANSACTION_IDENTIFIER_PATTERN);

  if (!match?.[2]) {
    return null;
  }

  return {
    type: match[1]?.toLowerCase() === 'bill' ? 'expense' : 'payout',
    reference: match[2].trim(),
  } as const;
};

const getTransactionDescription = (
  transaction: OwnerStatementSummariesDetailTransaction
) =>
  transaction.lines?.map((line) => line.description.trim()).find(Boolean) ||
  transaction.description.trim();

const getRowTransactionReferences = (title: string | undefined) => {
  const trimmedTitle = title?.trim();
  const identifier = getTransactionIdentifier(trimmedTitle);

  if (identifier?.reference) {
    return [identifier.reference];
  }

  if (trimmedTitle && TRANSACTION_REFERENCE_PATTERN.test(trimmedTitle)) {
    return [trimmedTitle];
  }

  return [];
};

const shouldEnrichTransactionRow = ({
  row,
  view,
}: {
  row: OwnerStatementSummariesDetailTableRow;
  view: OwnerStatementSummariesDetailData['resolvedView'];
}) => {
  if (!['reservation', 'bookingChannel'].includes(view)) {
    return false;
  }

  const hasDescriptionColumn = row.columns.some((column) =>
    isDescriptionColumnTitle(column.title)
  );
  const hasDateColumn = row.columns.some((column) =>
    isDateColumnTitle(column.title)
  );

  if (hasDescriptionColumn && hasDateColumn) {
    return false;
  }

  return isUUID(row.id) || getRowTransactionReferences(row.title).length > 0;
};

const getTransactionLookupKeys = (
  transaction: OwnerStatementSummariesDetailTransaction
) => {
  const keys = [transaction.id];

  if (transaction.uniqueRef?.trim()) {
    keys.push(transaction.uniqueRef.trim());
    keys.push(`${transaction.type}:${transaction.uniqueRef.trim()}`);
  }

  if (transaction.shortRef?.trim()) {
    keys.push(transaction.shortRef.trim());
    keys.push(`${transaction.type}:${transaction.shortRef.trim()}`);
  }

  return keys;
};

const getRowLookupKeys = (row: OwnerStatementSummariesDetailTableRow) => {
  const keys = isUUID(row.id) ? [row.id] : [];
  const identifier = getTransactionIdentifier(row.title);

  if (identifier?.reference) {
    keys.push(`${identifier.type}:${identifier.reference}`);
    keys.push(identifier.reference);
  }

  for (const reference of getRowTransactionReferences(row.title)) {
    if (!keys.includes(reference)) {
      keys.push(reference);
    }
  }

  return keys;
};

const getTransactionDateFormatted = (date: string) =>
  day(date).format('MMM D, YYYY');

const augmentRowWithTransactionDetails = ({
  row,
  transaction,
}: {
  row: OwnerStatementSummariesDetailTableRow;
  transaction: OwnerStatementSummariesDetailTransaction;
}) => {
  const description = getTransactionDescription(transaction);
  const columns = [...row.columns];

  if (
    description &&
    !columns.some((column) => isDescriptionColumnTitle(column.title))
  ) {
    columns.unshift({
      title: DESCRIPTION_COLUMN_TITLE,
      value: description,
      formatted: description,
      accountIds: [],
    });
  }

  if (
    transaction.date &&
    !columns.some((column) => isDateColumnTitle(column.title))
  ) {
    const descriptionIndex = columns.findIndex((column) =>
      isDescriptionColumnTitle(column.title)
    );

    columns.splice(descriptionIndex >= 0 ? descriptionIndex + 1 : 0, 0, {
      title: DATE_COLUMN_TITLE,
      value: transaction.date,
      formatted: getTransactionDateFormatted(transaction.date),
      accountIds: [],
    });
  }

  return {
    ...row,
    columns,
  } satisfies OwnerStatementSummariesDetailTableRow;
};

export const getOwnerStatementSummariesDetailLayout = ({
  data,
  layoutId,
}: {
  data: Pick<OwnerStatementSummariesDetailData, 'layouts'>;
  layoutId: string;
}) => {
  const layout = data.layouts.find((item) => item.layoutId === layoutId);

  if (!layout) {
    throw new Error(`Missing owner statement summary layout: ${layoutId}`);
  }

  return layout;
};

export const getOwnerStatementSummariesDetailSectionColumns = ({
  section,
}: {
  section: OwnerStatementSummariesDetailTableSection;
}) => {
  const seenTitles = new Set<string>();
  const rowsWithColumns = [...(section.rows ?? []), section.subtotal].filter(
    (
      row
    ): row is
      | OwnerStatementSummariesDetailTableRow
      | NonNullable<OwnerStatementSummariesDetailTableSection['subtotal']> =>
      Boolean(row)
  );

  const columns = rowsWithColumns
    .flatMap((row) => row.columns ?? [])
    .filter((column) => {
      const normalizedTitle = normalizeColumnTitle(column.title);

      if (!normalizedTitle) return false;
      if (seenTitles.has(normalizedTitle)) return false;

      seenTitles.add(normalizedTitle);
      return true;
    });

  return columns;
};

export const getOwnerStatementSummariesDetailTransactionLookup = ({
  data,
  view,
}: {
  data: OwnerStatementSummariesDetailData;
  view: OwnerStatementSummariesDetailData['resolvedView'];
}): OwnerStatementSummariesDetailTransactionLookup => {
  const ids = new Set<string>();
  const shortRefs = new Set<string>();
  const uniqueRefs = new Set<string>();

  for (const layout of data.layouts) {
    const sections = [
      ...layout.otherSections,
      ...(layout.payoutsSection ? [layout.payoutsSection] : []),
    ];

    for (const section of sections) {
      for (const row of section.rows ?? []) {
        if (!shouldEnrichTransactionRow({ row, view })) {
          continue;
        }

        if (isUUID(row.id)) {
          ids.add(row.id);
        }

        for (const reference of getRowTransactionReferences(row.title)) {
          shortRefs.add(reference);
          uniqueRefs.add(reference);
        }
      }
    }
  }

  return {
    ids: [...ids],
    shortRefs: [...shortRefs],
    uniqueRefs: [...uniqueRefs],
  };
};

export const mapOwnerStatementSummariesDetailTransactionsById = ({
  transactions,
}: {
  transactions: OwnerStatementSummariesDetailTransaction[];
}) => {
  const mapped: OwnerStatementSummariesDetailTransactionsById = {};

  for (const transaction of transactions) {
    for (const key of getTransactionLookupKeys(transaction)) {
      mapped[key] = transaction;
    }
  }

  return mapped;
};

export const augmentOwnerStatementSummariesDetailDataWithTransactions = ({
  data,
  transactionsById,
  view,
}: {
  data: OwnerStatementSummariesDetailData;
  transactionsById: OwnerStatementSummariesDetailTransactionsById;
  view: OwnerStatementSummariesDetailData['resolvedView'];
}) => {
  const augmentSection = (
    section: OwnerStatementSummariesDetailTableSection
  ) => ({
    ...section,
    rows: (section.rows ?? []).map((row) => {
      if (!shouldEnrichTransactionRow({ row, view })) {
        return row;
      }

      const transaction = getRowLookupKeys(row)
        .map((key) => transactionsById[key])
        .find(Boolean);

      return transaction
        ? augmentRowWithTransactionDetails({ row, transaction })
        : row;
    }),
  });

  return {
    ...data,
    layouts: data.layouts.map((layout) => ({
      ...layout,
      otherSections: layout.otherSections.map(augmentSection),
      payoutsSection: layout.payoutsSection
        ? augmentSection(layout.payoutsSection)
        : layout.payoutsSection,
    })),
  } satisfies OwnerStatementSummariesDetailData;
};

export const getOwnerStatementSummariesDetailPrimaryColumnTitle = ({
  columns,
}: {
  columns: OwnerStatementSummariesDetailTableRow['columns'];
}) => columns.find((column) => isDescriptionColumnTitle(column.title))?.title;

export const getOwnerStatementSummariesDetailFirstColumnHeader = ({
  primaryColumnTitle,
  sectionTitle,
  view,
}: {
  primaryColumnTitle?: string;
  sectionTitle?: string;
  view: OwnerStatementSummariesDetailData['resolvedView'];
}) => {
  if (
    isTransactionBreakdownView(view) &&
    isExpensesOrPayoutsSectionTitle(sectionTitle)
  ) {
    return sectionTitle?.trim() || sectionTitle || primaryColumnTitle;
  }

  return primaryColumnTitle;
};

export const getOwnerStatementSummariesDetailVisibleColumns = ({
  columns,
  primaryColumnTitle,
}: {
  columns: OwnerStatementSummariesDetailTableRow['columns'];
  primaryColumnTitle?: string;
}) =>
  primaryColumnTitle
    ? columns.filter(
        (column) =>
          normalizeColumnTitle(column.title) !==
          normalizeColumnTitle(primaryColumnTitle)
      )
    : columns;

export const getOwnerStatementSummariesDetailDisplayTitle = ({
  row,
  primaryColumnTitle,
}: {
  row: OwnerStatementSummariesDetailTableRow;
  primaryColumnTitle?: string;
}) => {
  const primaryColumn = primaryColumnTitle
    ? row.columns.find((column) => column.title === primaryColumnTitle)
    : undefined;

  if (primaryColumn?.formatted) {
    return primaryColumn.formatted;
  }

  if (typeof primaryColumn?.value === 'string' && primaryColumn.value.trim()) {
    return primaryColumn.value;
  }

  if (typeof primaryColumn?.value === 'number') {
    return String(primaryColumn.value);
  }

  return row.title || '-';
};
